/**
 * SILNIK RABATU (JS) - lustro backend/utils/vat.py.
 *
 * JEDNO zrodlo kwoty rabatu po stronie przegladarki: wyswietlacz, walidacja
 * platnosci i discount_amount wysylany do finalizacji licza TA SAMA funkcja,
 * ktora serwer ma w Pythonie (distribute_discount_by_vat +
 * effective_discount_total). Dzieki temu kwota pokazana kasjerce, kwota
 * pobrana od klienta i kwota zapisana w pos_transakcje.rabat_kwota to ta
 * sama liczba (ustalenia A i B wlasciciela), a blad walidacyjny serwera
 * (RABAT_REGULA_GROSZA) staje sie sytuacja wyjatkowa, nie normalna petla.
 *
 * PODSTAWA REGULY GROSZA - decyzja biznesowa wlasciciela (2026-08):
 * gratis/probka/promocja nigdy za 0.00, minimum 0.01 zl; towar z cena 0.00
 * nie wchodzi do koszyka; rabat nie moze sprowadzic pozycji do 0.00.
 * Protokol XML (pxml.txt) NIE stawia warunku > 0 - to nie jest wymog
 * drukarki. Kaucja 0.00 jest legalna i nie podlega tej regule.
 *
 * ARYTMETYKA: wylacznie GROSZE na liczbach calkowitych (bezpieczne do 2^53)
 * i dzielenie calkowite z zaokragleniem HALF_UP - zero floatow w petlach
 * rozdzialu, zeby wynik byl CO DO GROSZA identyczny z Decimal/ROUND_HALF_UP
 * po stronie Pythona. Kazda rozbieznosc JS-Python to blad (patrz test
 * wektorowy discountEngine.test.js).
 */

// Minimalna wartosc pozycji po rabacie: 1 grosz.
export const MIN_WARTOSC_POZYCJI_GR = 1;

/**
 * Zamien kwote w zlotych (number/string) na grosze (int).
 * HALF_UP dla wartosci dodatnich - identycznie z to_money/D po stronie Pythona.
 */
export const naGrosze = (kwota) => {
  const parsed = parseFloat(kwota);
  if (!Number.isFinite(parsed)) return 0;
  // Math.round dla ujemnych zaokragla w strone +inf (-0.5 -> 0), ale kwoty
  // ujemne i tak sa odrzucane/clampowane przed rozdzialem.
  return Math.round(parsed * 100);
};

export const naZlote = (grosze) => grosze / 100;

/**
 * Dzielenie num/den z zaokragleniem HALF_UP do liczby calkowitej.
 * Odpowiednik Decimal.quantize(0.01, ROUND_HALF_UP) na groszach.
 * Wylacznie dla num >= 0, den > 0 (tylko takie wystepuja w rozdziale).
 */
const dzielHalfUp = (num, den) => Math.floor((2 * num + den) / (2 * den));

/**
 * Najwiekszy rabat, jaki wolno naliczyc na pozycji o danej wartosci (w groszach).
 * Lustro utils/vat.max_rabat_pozycji: zostaje minimum 1 grosz;
 * pozycji za 0.01 lub mniej nie wolno rabatowac wcale.
 */
export const maxRabatPozycjiGr = (wartoscGr) =>
  wartoscGr <= MIN_WARTOSC_POZYCJI_GR ? 0 : wartoscGr - MIN_WARTOSC_POZYCJI_GR;

/**
 * Czy pozycja podlega rabatowi. Lustro is_rabatowalne z utils/vat.py:
 * kaucje (stawka -2 / typ 'kaucja' / is_deposit) i pozycje wykluczone
 * z rabatow NIE bierze udzialu w rozdziale. Kaucja 0.00 jest legalna -
 * po prostu nie jest rabatowana.
 */
export const jestRabatowalna = (item) => {
  const vat = parseFloat(item.stawka_vat ?? item.vat_rate ?? item.vat);
  if (vat === -2) return false;
  if (item.typ_produktu === 'kaucja' || item.is_deposit === true) return false;
  if (item.wykluczony_z_rabatow === 1 || item.wykluczony_z_rabatow === true) return false;
  return true;
};

/**
 * Wartosc brutto linii w groszach. Kolejnosc pol jak w utils/vat.py
 * ('wartosc_brutto') z zapasem na pozycje koszyka UI (price * quantity).
 */
export const wartoscLiniiGr = (item) => {
  if (item.wartosc_brutto !== undefined && item.wartosc_brutto !== null) {
    return naGrosze(item.wartosc_brutto);
  }
  const cena = parseFloat(item.price ?? item.cena_jednostkowa ?? item.cena) || 0;
  const ilosc = parseFloat(item.quantity ?? item.ilosc) || 0;
  return naGrosze(cena * ilosc);
};

/** Klucz grupy VAT - lustro str(items[i].get('stawka_vat', 23)). */
const kluczVat = (item) => {
  const raw = item.stawka_vat ?? item.vat_rate ?? item.vat;
  return String(raw === undefined || raw === null ? 23 : parseFloat(raw));
};

/**
 * Dosyp 'nadwyzke' rabatu pozycjom, ktore maja jeszcze zapas do limitu.
 * Lustro utils/vat._dosyp. Kolejnosc podaje wolajacy - zawsze OD KONCA.
 * Zwraca to, czego nie udalo sie nikomu oddac.
 */
const dosyp = (discounts, limity, kolejnosc, nadwyzka) => {
  if (nadwyzka <= 0) return 0;
  for (const idx of kolejnosc) {
    if (nadwyzka <= 0) break;
    const zapas = limity[idx] - discounts[idx];
    if (zapas <= 0) continue;
    const dodaj = Math.min(zapas, nadwyzka);
    discounts[idx] += dodaj;
    nadwyzka -= dodaj;
  }
  return nadwyzka;
};

/**
 * Odbierz 'deficyt' rabatu pozycjom, ktore juz jakis dostaly.
 * Lustro utils/vat._odbierz (poprawka usterki groszowej 2026-08: reszta
 * grupy/pozycji nigdy nie schodzi ponizej zera - nadmiar z zaokraglen
 * HALF_UP wczesniejszych grup jest odbierany od konca).
 */
const odbierz = (discounts, kolejnosc, deficyt) => {
  if (deficyt <= 0) return 0;
  for (const idx of kolejnosc) {
    if (deficyt <= 0) break;
    if (discounts[idx] <= 0) continue;
    const zabierz = Math.min(discounts[idx], deficyt);
    discounts[idx] -= zabierz;
    deficyt -= zabierz;
  }
  return deficyt;
};

/**
 * Rozloz rabat calkowity na pozycje pro-rata per stawka VAT.
 * Lustro utils/vat.distribute_discount_by_vat CO DO GROSZA:
 * te same grupy, ta sama kolejnosc reszt, ta sama regula grosza,
 * ta sama oslona przed grupa o wartosci 0.00 (dwa gratisy w jednej stawce
 * wywracaly finalizacje dzieleniem przez zero).
 *
 * @param {Array} items - pozycje ({wartosc_brutto|price+quantity, stawka_vat,
 *                        typ_produktu, is_deposit, wykluczony_z_rabatow})
 * @param {number} totalDiscount - kwota rabatu w ZLOTYCH
 * @returns {number[]} rabat na kazda pozycje w ZLOTYCH (0 dla nierabatowalnych)
 */
export const rozlozRabatPoStawkach = (items, totalDiscount) => {
  const zera = new Array(items ? items.length : 0).fill(0);
  const total = naGrosze(totalDiscount);
  if (total <= 0 || !items || items.length === 0) return zera;

  const rabatowalne = [];
  const wartosci = new Array(items.length).fill(0);
  for (let i = 0; i < items.length; i++) {
    wartosci[i] = wartoscLiniiGr(items[i]);
    if (jestRabatowalna(items[i])) rabatowalne.push(i);
  }
  if (rabatowalne.length === 0) return zera;

  const totalBrutto = rabatowalne.reduce((s, i) => s + wartosci[i], 0);
  if (totalBrutto <= 0) return zera;

  // Grupowanie po stawce VAT - Map pamieta kolejnosc wstawiania,
  // identycznie jak dict w Pythonie.
  const grupy = new Map();
  for (const i of rabatowalne) {
    const k = kluczVat(items[i]);
    if (!grupy.has(k)) grupy.set(k, []);
    grupy.get(k).push(i);
  }

  const discounts = new Array(items.length).fill(0);
  let allocated = 0;
  const limity = {};
  for (const i of rabatowalne) limity[i] = maxRabatPozycjiGr(wartosci[i]);
  let nadwyzkaGlobalna = 0;

  // Oslona 0/0: grupa warta 0.00 nie bierze udzialu w rozdziale.
  const gruposBrutto = new Map();
  for (const [k, idxs] of grupy) {
    gruposBrutto.set(k, idxs.reduce((s, i) => s + wartosci[i], 0));
  }
  const aktywneKeys = [...grupy.keys()].filter(k => gruposBrutto.get(k) > 0);
  if (aktywneKeys.length === 0) return zera;

  const bazaBrutto = aktywneKeys.reduce((s, k) => s + gruposBrutto.get(k), 0);

  aktywneKeys.forEach((vatKey, gi) => {
    const groupBrutto = gruposBrutto.get(vatKey);
    // Pozycja warta 0.00 nie wchodzi do rozdzialu w grupie (limit = 0)
    // i nie moze dostac reszty grupy jako ostatnia w kolejce.
    const indices = grupy.get(vatKey).filter(i => wartosci[i] > 0);

    let groupDiscount;
    if (gi === aktywneKeys.length - 1) {
      groupDiscount = total - allocated;
      if (groupDiscount < 0) {
        // Reszta nigdy nie schodzi ponizej zera: nadmiar z zaokraglen
        // wczesniejszych grup odbieramy ich pozycjom od konca.
        const wczesniejsze = [];
        for (let g = 0; g < gi; g++) {
          for (const i of grupy.get(aktywneKeys[g])) wczesniejsze.push(i);
        }
        odbierz(discounts, wczesniejsze.reverse(), -groupDiscount);
        groupDiscount = 0;
      }
    } else {
      groupDiscount = dzielHalfUp(total * groupBrutto, bazaBrutto);
    }

    let groupAllocated = 0;
    indices.forEach((idx, ii) => {
      let itemDiscount;
      if (ii === indices.length - 1) {
        itemDiscount = groupDiscount - groupAllocated;
        if (itemDiscount < 0) {
          // Ta sama usterka pietro nizej - ujemna reszte zerujemy,
          // nadmiar odbieramy wczesniejszym pozycjom grupy od konca.
          odbierz(discounts, indices.slice(0, -1).reverse(), -itemDiscount);
          itemDiscount = 0;
        }
      } else {
        itemDiscount = dzielHalfUp(groupDiscount * wartosci[idx], groupBrutto);
        groupAllocated += itemDiscount;
      }
      discounts[idx] = itemDiscount;
    });

    // TWARDA REGULA GROSZA (decyzja biznesowa wlasciciela, patrz naglowek):
    // po rabacie na pozycji musi zostac >= 0.01. Grosze sciete z jednej
    // pozycji przejmuja pozycje z zapasem - od ostatniej wstecz.
    let nadwyzka = 0;
    for (const idx of indices) {
      if (discounts[idx] > limity[idx]) {
        nadwyzka += discounts[idx] - limity[idx];
        discounts[idx] = limity[idx];
      }
    }
    nadwyzka = dosyp(discounts, limity, [...indices].reverse(), nadwyzka);

    if (gi !== aktywneKeys.length - 1) {
      // Reszta dla ostatniej grupy liczona od tego, co FAKTYCZNIE rozdano.
      allocated += indices.reduce((s, i) => s + discounts[i], 0);
    } else {
      nadwyzkaGlobalna = nadwyzka;
    }
  });

  // Grosze, ktorych zadna grupa nie przyjela, ida na pozycje z zapasem
  // - od ostatniej wstecz (moze przeniesc grosz miedzy stawkami VAT,
  // ale alternatywa - zgubic go - rozjechalaby kwote pobrana z paragonem).
  dosyp(discounts, limity, [...rabatowalne].reverse(), nadwyzkaGlobalna);

  return discounts.map(naZlote);
};

/**
 * Kwota rabatu, ktora FAKTYCZNIE da sie naliczyc na tych pozycjach.
 * Lustro utils/vat.effective_discount_total: iteruje do punktu stalego,
 * wynik <= zadanej kwoty, nigdy ujemny, a rozdzial wyniku sumuje sie
 * do niego co do grosza.
 *
 * @returns {number} osiagalna kwota rabatu w ZLOTYCH
 */
export const osiagalnyRabat = (items, totalDiscount, maxIteracji = 10) => {
  let total = naGrosze(totalDiscount);
  if (total <= 0 || !items || items.length === 0) return 0;

  for (let i = 0; i < maxIteracji; i++) {
    const rozdzielone = rozlozRabatPoStawkach(items, naZlote(total));
    const suma = rozdzielone.reduce((s, x) => s + naGrosze(x), 0);
    if (suma >= total) return naZlote(total);
    total = suma;
    if (total <= 0) return 0;
  }
  return naZlote(total);
};

/**
 * GLOWNA FUNKCJA KONTRAKTU (sekcja 1 planu rundy 4):
 * jedna liczba dla wyswietlacza, walidacji platnosci, zapisu rabat_kwota
 * i rozdzialu na paragonie.
 *
 * @param {Array} pozycje - pozycje koszyka (jak wyzej)
 * @param {{typ: ('procentowy'|'kwotowy'), wartosc: number}} rabat
 * @returns {{kwota_rabatu: number, baza: number, przyciety: boolean,
 *            powod: (null|'CLAMP_DO_BAZY'|'REGULA_GROSZA'|'BAZA_ZERO')}}
 */
export const obliczRabatKoszyka = (pozycje, rabat) => {
  const items = Array.isArray(pozycje) ? pozycje : [];
  const bazaGr = items.reduce(
    (s, it) => s + (jestRabatowalna(it) ? wartoscLiniiGr(it) : 0), 0);

  if (bazaGr <= 0) {
    return { kwota_rabatu: 0, baza: naZlote(Math.max(0, bazaGr)), przyciety: false, powod: 'BAZA_ZERO' };
  }

  const wartosc = parseFloat(rabat && rabat.wartosc) || 0;
  let zadanyGr;
  if (rabat && rabat.typ === 'procentowy') {
    // round_half_up(baza * procent / 100, 2) - w groszach: baza_gr * procent / 100.
    // Procent moze byc ulamkowy (np. 12.5) - skalujemy do intow x1000,
    // zeby dzielenie HALF_UP zostalo na liczbach calkowitych.
    const procentTys = Math.round(wartosc * 1000);
    zadanyGr = procentTys <= 0 ? 0 : dzielHalfUp(bazaGr * procentTys, 100 * 1000);
  } else {
    zadanyGr = naGrosze(wartosc);
  }

  // Clamp do [0, baza]
  let przyciety = false;
  let powod = null;
  if (zadanyGr < 0) zadanyGr = 0;
  if (zadanyGr > bazaGr) {
    zadanyGr = bazaGr;
    przyciety = true;
    powod = 'CLAMP_DO_BAZY';
  }

  // Twarda regula grosza - rozdzial tym samym algorytmem co paragon.
  const osiagalnyGr = naGrosze(osiagalnyRabat(items, naZlote(zadanyGr)));
  if (osiagalnyGr < zadanyGr) {
    przyciety = true;
    powod = 'REGULA_GROSZA';
  }

  return {
    kwota_rabatu: naZlote(osiagalnyGr),
    baza: naZlote(bazaGr),
    przyciety,
    powod,
  };
};

const discountEngine = {
  naGrosze,
  naZlote,
  maxRabatPozycjiGr,
  jestRabatowalna,
  wartoscLiniiGr,
  rozlozRabatPoStawkach,
  osiagalnyRabat,
  obliczRabatKoszyka,
};

export default discountEngine;
